import {
  ACTION,
  agGridMenuSeparator,
  AssetTransaction,
  AssetTransactionActionEnum,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_MAX_ROWS,
  ExportMenuItems,
  filterByCellValueMenuItem,
  filterByColumnMainMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useAssetTransactionColumns,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  WSBlotterTableMaxRecordsReachedWarning,
  type Column,
  type WebsocketRequest,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';

import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import type { GetContextMenuItemsParams, GetMainMenuItemsParams, RowDoubleClickedEvent } from 'ag-grid-community';
import { useRoleAuth } from '../../../hooks';
import {
  colIDToFilterBuilderKey,
  onlyServerFilterKeys,
  useAssetTransactionsFilter,
  type AssetTransactionsFilter,
} from './useAssetTransactionsFilter';
import { useModifyAssetTransactionDrawer } from './useModifyAssetTransactionDrawer';

export interface AssetTransactionsBlotterTableProps {
  blotterID: string;
  tabLabel?: string;
  defaultColumns?: (keyof AssetTransaction | Partial<Column>)[];
  defaultFilter?: AssetTransactionsFilter;
  initialIsOpen?: boolean;
  onCloneTab?: () => void;
}

function canModifyAssetTransaction(transaction: AssetTransaction | undefined, authorized: boolean): boolean {
  if (!authorized || transaction == null) {
    return false;
  }
  return transaction.TransactionAction !== AssetTransactionActionEnum.Canceled;
}

export function AssetTransactionsBlotterTable({
  blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  initialIsOpen,
  onCloneTab,
}: AssetTransactionsBlotterTableProps) {
  const { getConfig } = useOrgConfiguration();
  const mixpanel = useMixpanel();
  const { isAuthorized } = useRoleAuth();
  const authorizedToEdit = isAuthorized(ACTION.EDIT_POSITION);
  const { openModifyAssetTransactionDrawer } = useModifyAssetTransactionDrawer();
  const columns = useAssetTransactionColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useAssetTransactionsFilter({
    persistedBlotterTable,
  });

  const { blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause: filterBuilderAccordion.openClause,
      mixpanel,
    });
  });

  const openModifyIfAllowed = useCallbackRef((transaction: AssetTransaction | undefined) => {
    if (!canModifyAssetTransaction(transaction, authorizedToEdit) || transaction == null) {
      return;
    }
    openModifyAssetTransactionDrawer(transaction);
  });

  const handleRowDoubleClick = useCallbackRef((event: RowDoubleClickedEvent<AssetTransaction>) => {
    openModifyIfAllowed(event.data);
  });

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<AssetTransaction>) => {
    const data = params.node?.data;
    const modifyMenuItem =
      canModifyAssetTransaction(data, authorizedToEdit) && data != null
        ? {
            name: 'Modify Transaction',
            icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
            action: () => openModifyIfAllowed(data),
          }
        : undefined;

    return compact([
      modifyMenuItem,
      modifyMenuItem ? agGridMenuSeparator : undefined,
      ...filterByCellValueMenuItem({
        params,
        filterableProperties: filterBuilderProps.properties,
        openClause: filterBuilderAccordion.openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
    ]);
  });

  const request = useMemo(
    () => ({
      tag: blotterID,
      name: AssetTransaction.StreamName,
      sort_by: '-TransactTime',
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable<WebsocketRequest, AssetTransaction>({
    request,
    rowID: 'rowID' satisfies keyof AssetTransaction,
    persistence: persistedBlotterTable,
    columns,
    getExtraMainMenuItems,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    contextMenuOptions: {
      getCustomContextMenuItems,
    },
    gridOptions: {
      onRowDoubleClicked: handleRowDoubleClick,
    },
    exportOptions: {
      name: AssetTransaction.BlotterExportName,
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.TransactTime} />
      <BlotterTable {...blotterTable} />
    </>
  );
}
